import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { GlossyCross } from "./GlossyCross";
import { MountainLine } from "./MountainLine";
import { TID } from "../lib/testIds";

const PHONE = "+41774069813";
const PHONE_DISPLAY = "+41 77 406 98 13";
const EMAIL = "info@swissalpenline.ch";
const WHATSAPP_URL = `https://wa.me/${PHONE.replace(/[^0-9]/g, "")}`;

// QR code (SVG) generated via free qrserver — no key needed
const qrUrl = (target) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&bgcolor=0a0a0a&color=e8e4da&data=${encodeURIComponent(target)}`;

export const Footer = () => {
  const { t, lang, setLang, langs } = useLang();
  const reserveUrl =
    typeof window !== "undefined" ? `${window.location.origin}/reservar` : "https://swissalpenline.ch/reservar";

  return (
    <footer className="relative bg-brand-bg border-t border-brand-border mt-32 overflow-hidden">
      <MountainLine className="absolute bottom-0 left-0 right-0 w-full h-16 pointer-events-none" opacity={0.5} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-14">
          <div className="md:col-span-2">
            <div className="mb-6">
              <img
                src="/media/logo.png"
                alt="Swiss AlpenLine"
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-brand-silver leading-relaxed max-w-md">
              {t.hero.subtitle}
            </p>
            <div className="hairline mt-8 max-w-xs" />
            <p className="text-xs tracking-widest2 uppercase text-brand-dim mt-6">
              {t.contact.hours}
            </p>
          </div>

          <div>
            <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-6">{t.nav.contact}</p>
            <ul className="space-y-4">
              <li>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid={TID.footer.whatsapp}
                   className="flex items-center gap-3 text-sm text-brand-platinum link-underline">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </li>
              <li>
                <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">Address</p>
                <p className="text-sm text-brand-platinum">29 Bahnhofstrasse<br/>6162 Entlebuch · Luzern · Switzerland</p>
              </li>
              <li>
                <a href={`tel:${PHONE}`} className="flex items-center gap-3 text-sm text-brand-platinum link-underline">
                  <Phone size={16} /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} data-testid={TID.footer.email}
                   className="flex items-center gap-3 text-sm text-brand-platinum link-underline">
                  <Mail size={16} /> {EMAIL}
                </a>
              </li>
              <li>
                <span className="flex items-center gap-3 text-sm text-brand-dim">
                  <Instagram size={16} /> —
                </span>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-6">{t.footer.qr}</p>
            <div className="inline-block p-3 border border-brand-border bg-brand-surface" data-testid={TID.footer.qr}>
              <img src={qrUrl(reserveUrl)} alt="QR reservar" width={140} height={140} className="block" />
            </div>
            <div className="mt-8">
              <p className="text-xs tracking-widest2 uppercase text-brand-silver mb-3">{t.footer.langs}</p>
              <div className="flex flex-wrap gap-2" data-testid={TID.footer.lang}>
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1 text-[11px] tracking-widest2 uppercase border transition-colors ${
                      lang === l.code
                        ? "border-brand-platinum text-brand-platinum"
                        : "border-brand-border text-brand-silver hover:text-brand-platinum"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="hairline mt-16" />
        <div className="flex flex-col md:flex-row items-center justify-between mt-8 gap-4">
          <p className="text-xs text-brand-dim tracking-widest2 uppercase">
            © {new Date().getFullYear()} SwissAlpenLine — {t.footer.rights}
          </p>
          <div className="flex items-center gap-6 flex-wrap">
            <Link to="/tarifario" className="text-xs text-brand-silver hover:text-brand-platinum tracking-widest2 uppercase transition-colors">Tariff</Link>
            <Link to="/termos-e-condicoes" className="text-xs text-brand-silver hover:text-brand-platinum tracking-widest2 uppercase transition-colors">Terms</Link>
            <Link to="/direitos-de-autor" className="text-xs text-brand-silver hover:text-brand-platinum tracking-widest2 uppercase transition-colors">Copyright</Link>
            <p className="text-xs text-brand-dim tracking-widest2 uppercase">swissalpenline.ch</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
